import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { Badge, Dropdown, List, Button, Empty, Spin, Typography, Tag, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';
import { resolveNotification } from '../../../utils/notificationResolver';

const { Text } = Typography;

interface NotificationItem {
  id: string;
  category?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface ApiResult<T = unknown> {
  success: boolean;
  data?: T;
  [key: string]: unknown;
}

interface NotificationService {
  getUnreadCount: () => Promise<ApiResult<{ count?: number }>>;
  getNotifications: (params?: { page_size?: number; direction?: 'asc' | 'desc' }) => Promise<ApiResult<NotificationItem[]>>;
  setRead: (ids: string[]) => Promise<ApiResult>;
  readAll: () => Promise<ApiResult>;
  [key: string]: unknown;
}

// notificationData 透過 NotificationChannel 推送，同一個 Record 內可能包含
// 多種 payload（job_status / new_notification 等），所以這裡以 unknown 接受，
// 內部以 runtime narrow（data.type / data.notification）區分。
type NotificationDataMap = Record<string, unknown>;

interface NotificationBellProps {
  service: NotificationService;
  role?: 'admin' | 'operator';
  notificationData?: NotificationDataMap | null;
}

/**
 * 通知小鈴鐺組件（Shared）
 *
 * @param role - 角色標識（'admin' 或 'operator'）
 * @param notificationData - 來自 NotificationChannel 的即時通知資料（包含 job_status 和 new_notification）
 */
export default function NotificationBell({ service, role: _role = 'admin', notificationData }: NotificationBellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // 獲取未讀數量
  const fetchUnreadCount = useCallback(async () => {
    try {
      const result = await service.getUnreadCount();
      if (result.success) {
        setUnreadCount(result.data?.count || 0);
      }
    } catch (error) {
      console.error('[NotificationBell] Failed to fetch unread count:', error);
    }
  }, [service]);

  // 獲取最新通知列表（前 5 條）
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const result = await service.getNotifications({ page_size: 5, direction: 'desc' });
      if (result.success) {
        setNotifications(result.data || []);
      }
    } catch (error) {
      console.error('[NotificationBell] Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [service]);

  // 標記單條為已讀
  const markAsRead = useCallback(async (id: string) => {
    try {
      const result = await service.setRead([id]);
      if (result.success) {
        // 更新本地狀態
        setNotifications(prev => prev.filter(n => n.id !== id));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('[NotificationBell] Failed to mark as read:', error);
    }
  }, [service]);

  // 標記全部已讀
  const markAllAsRead = useCallback(async () => {
    try {
      const result = await service.readAll();
      if (result.success) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('[NotificationBell] Failed to mark all as read:', error);
    }
  }, [service]);

  // 初始載入未讀數量
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // 下拉框打開時載入通知列表
  useEffect(() => {
    if (dropdownOpen) {
      fetchNotifications();
    }
  }, [dropdownOpen, fetchNotifications]);

  // 監聽來自 NotificationChannel 的新通知
  const processedKeysRef = useRef(new Set<string>());
  useEffect(() => {
    if (!notificationData || typeof notificationData !== 'object') return;

    Object.keys(notificationData).forEach((key) => {
      if (processedKeysRef.current.has(key)) return;
      processedKeysRef.current.add(key);

      const data = notificationData[key] as { type?: string; notification?: NotificationItem } | null | undefined;
      if (data && data.type === 'new_notification' && data.notification) {
        const notification = data.notification;

        setUnreadCount(prev => prev + 1);

        // 如果下拉框是打開的，添加到通知列表頂部
        setNotifications(prev => {
          if (!dropdownOpen) return prev;
          const exists = prev.some(n => n.id === notification.id);
          if (exists) return prev;
          return [notification, ...prev].slice(0, 5);
        });

        const { title: resolvedTitle } = resolveNotification(notification);
        message.info({ content: resolvedTitle, duration: 3 });
      }
    });
  }, [notificationData, dropdownOpen]);

  // 分類標籤顏色映射
  const getCategoryColor = useCallback((category: string | undefined) => {
    const colorMap: Record<string, string> = {
      system: 'blue',
      order: 'green',
      inbound: 'cyan',
      stock: 'orange',
      platform: 'magenta'
    };
    return (category && colorMap[category]) || 'default';
  }, []);

  // 渲染通知項目
  const renderNotificationItem = useCallback((item: NotificationItem) => {
    const { title, description } = resolveNotification(item);

    const handleClick = () => {
      markAsRead(item.id);
      setDropdownOpen(false);
      navigate(`/notifications/${item.id}`);
    };

    return (
      <List.Item
        key={item.id}
        style={{
          cursor: 'pointer',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0'
        }}
        onClick={handleClick}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {/* 標題與操作按鈕 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div
              style={{
                flex: 1,
                marginRight: '8px',
                fontWeight: 500,
                fontSize: '14px'
              }}
            >
              {title || '-'}
            </div>
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                markAsRead(item.id);
              }}
            />
          </div>

          {/* 內容預覽 */}
          {description && (
            <div
              style={{
                fontSize: '13px',
                color: '#666',
                lineHeight: '1.5',
                maxHeight: '60px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {description}
            </div>
          )}

          {/* 分類、時間與 Read More */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space size="small">
              <Tag color={getCategoryColor(item.category)}>
                {t(`notifications.categories.${item.category}`)}
              </Tag>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {formatDateTime(item.created_at)}
              </Text>
            </Space>
            <Link
              to={`/notifications/${item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen(false);
              }}
              style={{
                fontSize: '12px',
                textDecoration: 'none'
              }}
            >
              {t('notifications.bell.read_more')}
            </Link>
          </div>
        </Space>
      </List.Item>
    );
  }, [markAsRead, getCategoryColor, t, navigate]);

  // 下拉菜單內容
  const dropdownMenu = useMemo(() => (
    <div style={{
      width: 450,
      maxHeight: 600,
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      borderRadius: '4px'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Text strong>{t('notifications.bell.title')}</Text>
        <Space size="small">
          {notifications.length > 0 && (
            <Button
              type="link"
              size="small"
              onClick={markAllAsRead}
            >
              {t('notifications.bell.mark_all_read')}
            </Button>
          )}
          <Button
            type="link"
            size="small"
            onClick={() => {
              setDropdownOpen(false);
              navigate('/notifications');
            }}
          >
            {t('notifications.bell.view_all')}
          </Button>
        </Space>
      </div>

      {/* Body */}
      <div style={{ maxHeight: 500, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('notifications.bell.no_notifications')}
            style={{ padding: '40px' }}
          />
        ) : (
          <List
            dataSource={notifications}
            renderItem={renderNotificationItem}
            split={false}
          />
        )}
      </div>
    </div>
  ), [loading, notifications, markAllAsRead, navigate, renderNotificationItem, t]);

  return (
    <Dropdown
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      popupRender={() => dropdownMenu}
      trigger={['click']}
      placement="bottomRight"
    >
      <Badge count={unreadCount} offset={[-5, 5]} style={{ cursor: 'pointer' }}>
        <BellOutlined style={{ fontSize: '18px', cursor: 'pointer' }} />
      </Badge>
    </Dropdown>
  );
}
